import { useRef, useEffect, useState, useCallback, useMemo, forwardRef, useImperativeHandle } from "react";
import { useYouTubePlayer } from "@/hooks/use-youtube-player";
import {
    type VideoPlatform,
    getEmbedUrl,
    getPlatformPlayerConfig,
    supportsTimeSync,
    getPlatformDisplayName,
} from "@/lib/video-types";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertCircle, Video } from "lucide-react";

/**
 * React escapes attribute values but does not block `javascript:` in an href, so
 * a stored URL is only rendered as a link when it is a plain web URL.
 * `videos.url` is normally written by the validated pipeline; this is the guard
 * for any future writer that is not.
 */
function safeExternalHref(url: string | null | undefined): string | undefined {
  if (!url) return undefined;
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:" ? parsed.toString() : undefined;
  } catch {
    return undefined;
  }
}


/**
 * How long a warming player runs before it is paused: enough to hold the first
 * seconds of the line it was built on, not enough to stream the video in the
 * background.
 */
const PRELOAD_BUFFER_MS = 4000;

/**
 * A promoted player starts muted (that play is always permitted) and only then
 * gets its sound back, which does not need a gesture of its own.
 */
const UNMUTE_DELAY_MS = 250;


interface MultiPlatformPlayerProps {
    /** Platform-specific video ID */
    videoId: string;
    /** Video platform */
    platform: VideoPlatform;
    /** Original video URL (needed for Facebook/X embeds) */
    originalUrl?: string;
    /**
     * Seek here once the player is ready, and start playing.
     *
     * A clip is a moment, not a video: opening a search hit has to land on the
     * line that matched. Passing it as a prop (rather than calling
     * `ref.seekTo()` from the parent) is what makes it survive the player being
     * rebuilt for a new video — a ref call made before `onReady` is a no-op, and
     * the parent has no reliable way to know when that is.
     */
    startTime?: number;
    /**
     * False while this player is only *warming* the next clip.
     *
     * A warming player is mounted off-screen, muted (the one kind of playback a
     * browser starts on its own) at the clip's first second, and paused again
     * once it has buffered, so pressing "next" swaps in a player that already
     * holds the line instead of building one and waiting for it.
     */
    active?: boolean;
    /** Callback for time updates (only works for YouTube) */
    onTimeUpdate?: (time: number) => void;
    /** Callback for play state changes (only works for YouTube) */
    onStateChange?: (isPlaying: boolean) => void;
    /** Custom class name */
    className?: string;
}

interface YouTubePlayerControls {
    containerId: string;
    isReady: boolean;
    isPlaying: boolean;
    currentTime: number;
    duration: number;
    play: () => void;
    pause: () => void;
    toggle: () => void;
    seekTo: (seconds: number) => void;
}

/** Ref interface for controlling the player from parent components */
export interface MultiPlatformPlayerRef {
    seekTo: (seconds: number) => void;
    play: () => void;
    pause: () => void;
    toggle: () => void;
    getCurrentTime: () => number;
    /** No-op off YouTube: only the IFrame API exposes a rate. */
    setPlaybackRate: (rate: number) => void;
}

/**
 * Multi-platform video player component
 * Uses YouTube IFrame API for YouTube videos, and standard iframes for other platforms
 */
export const MultiPlatformPlayer = forwardRef<MultiPlatformPlayerRef, MultiPlatformPlayerProps>(function MultiPlatformPlayer({
    videoId,
    platform,
    originalUrl,
    startTime,
    active = true,
    onTimeUpdate,
    onStateChange,
    className = "",
}, ref) {
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const iframeRef = useRef<HTMLIFrameElement>(null);

    // Use YouTube player hook for YouTube videos
    const youtubePlayer = useYouTubePlayer(
        platform === "youtube" ? videoId : null,
        {
            // Only the clip on screen reports: a warming player would otherwise
            // overwrite the page's play state and caption clock with its own.
            onTimeUpdate: active ? onTimeUpdate : undefined,
            onStateChange: active ? onStateChange : undefined,
            // Built at the clip's first second, and playing from there, so the
            // iframe starts on the line instead of loading 0:00 and stalling
            // again on a seek after `onReady`.
            startAt: startTime ?? null,
            startMuted: !active,
        }
    );

    // Apply `startTime` once, after the player for *this* video is ready.
    // Keyed on the build as well as the clip: a rebuilt player is a different
    // player, and a clip already opened earlier has to be applied to it again
    // (Next then Previous used to leave the new player on the play button).
    const appliedStartRef = useRef<string | null>(null);
    /** True once this player has spent time as the one warming the next clip. */
    const wasWarmingRef = useRef(!active);
    const {
        isReady: playerReady,
        isPlaying: playerPlaying,
        seekTo: playerSeek,
        play: playerPlay,
        pause: playerPause,
        unMute: playerUnMute,
        buildToken,
        builtStart,
    } = youtubePlayer;

    useEffect(() => {
        if (!active) wasWarmingRef.current = true;
    }, [active]);

    useEffect(() => {
        if (platform !== "youtube" || !playerReady) return;
        // A warming player was already started (silently) by the embed itself;
        // it is applied when it is promoted, not before.
        if (!active) return;
        if (startTime == null || !Number.isFinite(startTime)) return;

        const key = `${buildToken}:${videoId}@${startTime}`;
        if (appliedStartRef.current === key) return;

        // The embed was built sitting on this clip, so there is nothing to seek:
        // seeking again would only throw away the buffer it already has and
        // start the stall over. A player that warmed this clip has drifted (or
        // been paused mid-line), so it does get moved back to the first word,
        // which is cheap now that it holds the data.
        const builtAtClip = builtStart != null && Math.abs(builtStart - startTime) < 1;
        const needsSeek = !builtAtClip || wasWarmingRef.current;
        if (needsSeek && !playerSeek(startTime)) return;

        appliedStartRef.current = key;
        playerPlay();
    }, [platform, videoId, startTime, active, playerReady, playerSeek, playerPlay, buildToken, builtStart]);

    // Buffer the next clip, then stop: it only has to hold the first seconds of
    // its line, and a muted iframe streaming a two-hour talk in the background
    // is someone's bandwidth.
    useEffect(() => {
        if (active || !playerPlaying) return;
        const timer = window.setTimeout(() => playerPause(), PRELOAD_BUFFER_MS);
        return () => window.clearTimeout(timer);
    }, [active, playerPlaying, playerPause]);

    // Sound comes back after the promoted player has started, so the play
    // command itself stays the muted (always allowed) one.
    useEffect(() => {
        if (!active) return;
        const timer = window.setTimeout(() => playerUnMute(), UNMUTE_DELAY_MS);
        return () => window.clearTimeout(timer);
    }, [active, buildToken, playerUnMute]);

    // Expose player controls via ref for YouTube videos
    useImperativeHandle(ref, () => ({
        seekTo: (seconds: number) => {
            if (platform === "youtube") {
                youtubePlayer.seekTo(seconds);
            }
        },
        play: () => {
            if (platform === "youtube") {
                youtubePlayer.play();
            }
        },
        pause: () => {
            if (platform === "youtube") {
                youtubePlayer.pause();
            }
        },
        toggle: () => {
            if (platform === "youtube") {
                youtubePlayer.toggle();
            }
        },
        getCurrentTime: () => {
            if (platform === "youtube") {
                return youtubePlayer.currentTime;
            }
            return 0;
        },
        setPlaybackRate: (rate: number) => {
            if (platform === "youtube") {
                youtubePlayer.setPlaybackRate(rate);
            }
        },
    }), [platform, youtubePlayer]);

    // Get platform-specific configuration (memoized to prevent re-renders)
    const config = useMemo(
        () => getPlatformPlayerConfig(platform),
        [platform]
    );

    // Handle iframe load for non-YouTube platforms
    const handleIframeLoad = useCallback(() => {
        setIsLoading(false);
        setHasError(false);
    }, []);

    const handleIframeError = useCallback(() => {
        setIsLoading(false);
        setHasError(true);
    }, []);

    // Reset loading state when videoId or platform changes
    useEffect(() => {
        if (platform !== "youtube") {
            setIsLoading(true);
            setHasError(false);
        }
    }, [videoId, platform]);

    // For YouTube, use the dedicated player
    if (platform === "youtube") {
        return (
            <div className={`relative ${className}`}>
                <div
                    className="w-full bg-black rounded-lg overflow-hidden"
                    style={{ aspectRatio: config.aspectRatio }}
                >
                    <div id={youtubePlayer.containerId} className="w-full h-full" />
                </div>
                {!youtubePlayer.isReady && (
                    <div className="absolute inset-0 flex items-center justify-center bg-muted rounded-lg">
                        <Skeleton className="w-full h-full absolute inset-0" />
                        <Video className="h-12 w-12 text-muted-foreground animate-pulse" />
                    </div>
                )}
            </div>
        );
    }

    // For other platforms, use iframe embed
    const embedUrl = getEmbedUrl(videoId, platform, originalUrl);

    if (!embedUrl) {
        return (
            <div
                className={`flex flex-col items-center justify-center bg-muted rounded-lg ${className}`}
                style={{ aspectRatio: config.aspectRatio, minHeight: config.minHeight }}
            >
                <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground text-center px-4">
                    Unable to embed this {getPlatformDisplayName(platform)} video.
                    <br />
                    <a
                        href={safeExternalHref(originalUrl)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                    >
                        Open in {getPlatformDisplayName(platform)}
                    </a>
                </p>
            </div>
        );
    }

    return (
        <div className={`relative ${className}`}>
            {/* Loading state */}
            {isLoading && !hasError && (
                <div
                    className="absolute inset-0 flex items-center justify-center bg-muted rounded-lg z-10"
                    style={{ aspectRatio: config.aspectRatio }}
                >
                    <Skeleton className="w-full h-full absolute inset-0" />
                    <Video className="h-12 w-12 text-muted-foreground animate-pulse" />
                </div>
            )}

            {/* Error state */}
            {hasError && (
                <div
                    className="flex flex-col items-center justify-center bg-muted rounded-lg"
                    style={{ aspectRatio: config.aspectRatio, minHeight: config.minHeight }}
                >
                    <AlertCircle className="h-12 w-12 text-destructive mb-4" />
                    <p className="text-muted-foreground text-center px-4">
                        Failed to load video.
                        <br />
                        <a
                            href={safeExternalHref(originalUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                        >
                            Open in {getPlatformDisplayName(platform)}
                        </a>
                    </p>
                </div>
            )}

            {/* Iframe embed */}
            {!hasError && (
                <iframe
                    ref={iframeRef}
                    src={embedUrl}
                    title={`${getPlatformDisplayName(platform)} video player`}
                    className="w-full rounded-lg"
                    style={{
                        aspectRatio: config.aspectRatio,
                        minHeight: config.minHeight,
                        display: isLoading ? "none" : "block",
                    }}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen={config.allowFullscreen}
                    onLoad={handleIframeLoad}
                    onError={handleIframeError}
                />
            )}

            {/* No time sync badge for non-YouTube platforms */}
            {!supportsTimeSync(platform) && !isLoading && !hasError && (
                <div className="absolute bottom-2 left-2">
                    <Badge variant="secondary" className="bg-black/70 text-white text-xs">
                        Manual caption sync
                    </Badge>
                </div>
            )}
        </div>
    );
});

MultiPlatformPlayer.displayName = "MultiPlatformPlayer";

/**
 * Hook to get player controls for YouTube videos
 * Returns the controls from useYouTubePlayer when platform is YouTube
 */
export function useMultiPlatformPlayer(
    videoId: string | null,
    platform: VideoPlatform | null,
    options: {
        onTimeUpdate?: (time: number) => void;
        onStateChange?: (isPlaying: boolean) => void;
    } = {}
): YouTubePlayerControls | null {
    const youtubePlayer = useYouTubePlayer(
        platform === "youtube" ? videoId : null,
        options
    );

    if (platform === "youtube") {
        return youtubePlayer;
    }

    // For non-YouTube platforms, return null (no programmatic control)
    return null;
}
